'use client';

import React, { FC } from 'react';

import { GenNavTile } from '@/components';

import { TypeAccountNavTileFields } from '@/core/generated-types';

import { TypeUserRecord } from '@/lib/api/accountManager/types';

import { TypeProduct } from '@/lib/api/productCatalog/types';

import {
  decorateScheduledItemWithProductData,
  getScheduledItems,
} from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';

import { isSubscriptionActive } from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

import { TypeSubscriptionRecord } from '@/lib/api/subscriptionManager/types';

import formatPossessiveProperNoun from '@/utils/translations/formatPossessiveProperNoun';
import interpolate from '@/utils/translations/interpolate';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';

import { _findChildForSubscription } from '../utils';

import NextPlayKitDetails from './NextPlayKitDetails';
import SubscriptionActions from './SubscriptionActions';
import PromoCreditsBanner from '@/components/account/_components/PromoCreditsBanner/PromoCreditsBanner';

export type ManageSubscriptionsTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  subscriptions: TypeSubscriptionRecord[] | undefined;
  playKitSubscriptionProduct: TypeProduct | undefined;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
  isLanguageSettingsActionEnabled: boolean;
};

type ManageSubscriptionProps = {
  subscription: TypeSubscriptionRecord;
  isCancelSaverProgramEnabled: boolean;
};

const ManageSubscriptionsTile: FC<ManageSubscriptionsTileProps> = ({
  t,
  user,
  subscriptions,
  playKitSubscriptionProduct,
  tile,
  locale,
  isLanguageSettingsActionEnabled,
}) => {
  const isCancelSaverProgramEnabled = ['US', 'UK', 'AU'].includes(
    process.env.NEXT_PUBLIC_REGION as string,
  );

  if (!subscriptions || subscriptions.length === 0) return null;

  if (!playKitSubscriptionProduct) return null;

  const NoUpcomingScheduledItemsNotice: FC = () => {
    return <p>{t.noUpcomingKitsMessage}</p>;
  };

  const ManageSubscription: FC<ManageSubscriptionProps> = ({
    subscription,
    isCancelSaverProgramEnabled,
  }) => {
    const unprocessedScheduledItems = getScheduledItems(subscription, {
      filterProcessedItems: true,
    });

    const nextUnprocessedItem = unprocessedScheduledItems?.[0];

    const hasUpcomingItems =
      isSubscriptionActive(subscription) && nextUnprocessedItem;

    const child = _findChildForSubscription(subscription, user);

    const productDecoratedItem = decorateScheduledItemWithProductData(
      nextUnprocessedItem,
      playKitSubscriptionProduct.products,
    );

    return (
      <div className="mb-8 border-b border-grey-midtone pb-8 last:mb-0 last:border-none">
        <PromoCreditsBanner
          userUuid={subscription.recipient_uuid}
          subscriptionUuid={subscription.uuid}
          message={t.playkitPromoCreditMessage}
          classes="mb-4"
        />

        {child && (
          <p className="mb-5 text-xl font-bold">
            {interpolate(t.childsPlayKits, {
              childName: formatPossessiveProperNoun(child.name, locale),
            })}
          </p>
        )}

        {hasUpcomingItems ? (
          <NextPlayKitDetails
            t={t}
            locale={locale}
            nextUnprocessedItem={productDecoratedItem}
          />
        ) : (
          <NoUpcomingScheduledItemsNotice />
        )}

        <SubscriptionActions
          t={t}
          locale={locale}
          user={user}
          subscription={subscription}
          productDecoratedItem={productDecoratedItem}
          nextUnprocessedItem={nextUnprocessedItem}
          isLanguageSettingsActionEnabled={isLanguageSettingsActionEnabled}
          isCancelSaverProgramEnabled={isCancelSaverProgramEnabled}
        />
      </div>
    );
  };

  return (
    <GenNavTile
      title={tile.title}
      subTitle={tile.subheader}
      description={tile.description}
    >
      {subscriptions.map((subscription) => (
        <ManageSubscription
          subscription={subscription}
          key={subscription.uuid}
          isCancelSaverProgramEnabled={isCancelSaverProgramEnabled}
        />
      ))}
    </GenNavTile>
  );
};

export default React.memo(ManageSubscriptionsTile);
